import React, { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

const INSTANT_SCROLL_BEHAVIOR = 'instant' as ScrollBehavior;
const SCROLL_RESET_INTERVAL_MS = 16;
const SCROLL_RESET_RETRY_DELAY_MS = 80;
const SCROLL_RESET_STOP_DELAY_MS = 220;
const HASH_SCROLL_RETRY_DELAY_MS = 150;

export function resolveHashTargetId(hash: string): string | null {
  if (!hash) {
    return null;
  }

  const rawHashId = hash.slice(1);

  try {
    return decodeURIComponent(rawHashId);
  } catch {
    // Malformed fragments must not crash the public frontpage.
    return rawHashId;
  }
}

export function ScrollToHash(): React.JSX.Element | null {
  const location = useLocation();

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    const runWithoutSmoothScroll = (callback: () => void): void => {
      const previousHtmlScrollBehavior =
        document.documentElement.style.scrollBehavior;
      const previousBodyScrollBehavior = document.body.style.scrollBehavior;

      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';

      callback();

      window.requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior =
          previousHtmlScrollBehavior;
        document.body.style.scrollBehavior = previousBodyScrollBehavior;
      });
    };

    const focusTopFrame = (): void => {
      const topFrame = document.getElementById('top');
      if (topFrame instanceof HTMLElement) {
        topFrame.focus({ preventScroll: true });
        return;
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    const scrollToTop = (): void => {
      runWithoutSmoothScroll(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: INSTANT_SCROLL_BEHAVIOR,
        });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    };

    if (!location.hash) {
      scrollToTop();
      focusTopFrame();

      const animationFrameId = window.requestAnimationFrame(() => {
        scrollToTop();
        focusTopFrame();
      });
      const intervalId = window.setInterval(() => {
        scrollToTop();
      }, SCROLL_RESET_INTERVAL_MS);
      const timeoutId = window.setTimeout(() => {
        scrollToTop();
        focusTopFrame();
      }, SCROLL_RESET_RETRY_DELAY_MS);
      const stopIntervalTimeoutId = window.setTimeout(() => {
        window.clearInterval(intervalId);
      }, SCROLL_RESET_STOP_DELAY_MS);

      return () => {
        window.cancelAnimationFrame(animationFrameId);
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
        window.clearTimeout(stopIntervalTimeoutId);
      };
    }

    const hashId = resolveHashTargetId(location.hash);
    if (hashId === null) {
      return;
    }

    const scrollToTarget = (): boolean => {
      const target = document.getElementById(hashId);
      if (!target) {
        return false;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    if (scrollToTarget()) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      scrollToTarget();
    });
    const timeoutId = window.setTimeout(() => {
      scrollToTarget();
    }, HASH_SCROLL_RETRY_DELAY_MS);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [location.hash, location.pathname]);

  return null;
}
